import type { ConversationStreamEvent } from "~/api/conversationStream";
import { moderatorRowId, streamRowId } from "~/api/conversationStream";

/**
 * The turn stream as state (slate-feature-transfer W8 §B.3).
 *
 * A pure reducer, because this is where kms's streaming bugs lived and none of them were
 * visible in a component: events it never handled (`skill_invocation`/`skill_result` arrived
 * only after the message finalised), a moderator row that could never be attributed
 * (`moderator_synthesis` is never emitted — `merged_response` is), and per-conversation state
 * that was never reset. Here the whole lifecycle is one function that a test can drive frame
 * by frame.
 *
 * Rows carry SYNTHETIC ids (`stream:<id>` / `moderator:<id>`) until the final message arrives;
 * `settledIds` then names the real message ids, so the transcript can drop a live row the
 * moment the refetched list contains its permanent twin.
 */

export interface StreamSkill {
  skillId: string;
  query: string | null;
  status: "running" | "done";
}

export interface StreamUsage {
  totalTokens: number | null;
  totalCost: number | null;
  model: string | null;
  durationMs: number | null;
}

export interface StreamRow {
  id: string;
  kind: "thinker" | "moderator";
  thinkerId: string | null;
  author: string;
  model: string | null;
  text: string;
  status: "streaming" | "done" | "empty" | "error";
  reason: string | null;
  skills: StreamSkill[];
  usage: StreamUsage | null;
  /** Set once the real message lands, so the row can retire when the refetch shows it. */
  settledId: string | null;
}

export interface StreamNotice {
  kind: "skipped" | "empty" | "error" | "early_stop" | "moderator" | "revision" | "unknown";
  text: string;
}

export interface StreamState {
  /** True from the first frame until `complete`, an `error`, or the promise settling. */
  running: boolean;
  rows: StreamRow[];
  notices: StreamNotice[];
  /** `{thinkerCount}` from `processing_start` — what the "N thinkers are answering" line reads. */
  thinkerCount: number;
  contextBytes: number | null;
  /** The moderator's evaluation round, when it is running one. */
  round: { current: number; max: number } | null;
  complete: boolean;
  error: string | null;
  settledIds: string[];
}

export function initialStreamState(): StreamState {
  return {
    running: false,
    rows: [],
    notices: [],
    thinkerCount: 0,
    contextBytes: null,
    round: null,
    complete: false,
    error: null,
    settledIds: [],
  };
}

const text = (value: unknown): string => (typeof value === "string" ? value : "");
const maybe = (value: unknown): string | null => (typeof value === "string" ? value : null);
const digits = (value: unknown): number | null => (typeof value === "number" ? value : null);

function upsert(
  rows: StreamRow[],
  id: string,
  seed: () => StreamRow,
  patch: (row: StreamRow) => StreamRow,
): StreamRow[] {
  const index = rows.findIndex((row) => row.id === id);
  if (index === -1) {
    return [...rows, patch(seed())];
  }
  const next = [...rows];
  const current = rows[index];
  if (current) {
    next[index] = patch(current);
  }
  return next;
}

const thinkerSeed = (id: string, thinkerId: string | null, author: string): StreamRow => ({
  id,
  kind: "thinker",
  thinkerId,
  author,
  model: null,
  text: "",
  status: "streaming",
  reason: null,
  skills: [],
  usage: null,
  settledId: null,
});

const moderatorSeed = (id: string): StreamRow => ({
  ...thinkerSeed(id, null, "Moderator"),
  kind: "moderator",
});

/**
 * One frame in, one state out. Every event name is handled; `unknown` becomes a notice rather
 * than nothing, which is the whole point of the exhaustive switch (W8-10).
 */
export function reduceStream(state: StreamState, event: ConversationStreamEvent): StreamState {
  const running = { ...state, running: true };
  const data = event.event === "unknown" ? event.data : event.data;

  switch (event.event) {
    case "message_created":
      return running;

    case "processing_start":
      return { ...running, thinkerCount: digits(data.thinkerCount) ?? 0 };

    case "context_info":
      return { ...running, contextBytes: digits(data.contextSizeBytes) };

    case "thinker_start": {
      const streamId = text(data.streamId);
      const id = streamRowId(streamId);
      return {
        ...running,
        rows: upsert(
          running.rows,
          id,
          () =>
            thinkerSeed(id, maybe(data.thinkerId), text(data.thinkerName) || text(data.thinkerId)),
          (row) => ({ ...row, model: maybe(data.model), status: "streaming" }),
        ),
      };
    }

    case "ai_delta":
    case "revision_delta": {
      const id = streamRowId(text(data.streamId));
      return {
        ...running,
        rows: upsert(
          running.rows,
          id,
          () => thinkerSeed(id, maybe(data.thinkerId), text(data.thinkerId)),
          (row) => ({ ...row, text: row.text + text(data.delta), status: "streaming" }),
        ),
      };
    }

    case "moderator_delta": {
      const id = moderatorRowId(text(data.streamId));
      return {
        ...running,
        rows: upsert(
          running.rows,
          id,
          () => moderatorSeed(id),
          (row) => ({ ...row, text: row.text + text(data.delta), status: "streaming" }),
        ),
      };
    }

    case "skill_invocation": {
      const id = streamRowId(text(data.streamId));
      const skillId = text(data.skillId);
      return {
        ...running,
        rows: upsert(
          running.rows,
          id,
          () => thinkerSeed(id, maybe(data.thinkerId), text(data.thinkerId)),
          (row) => ({
            ...row,
            skills: [...row.skills, { skillId, query: maybe(data.query), status: "running" }],
          }),
        ),
      };
    }

    case "skill_result": {
      const id = streamRowId(text(data.streamId));
      const skillId = text(data.skillId);
      return {
        ...running,
        rows: upsert(
          running.rows,
          id,
          () => thinkerSeed(id, maybe(data.thinkerId), text(data.thinkerId)),
          (row) => ({
            ...row,
            skills: row.skills.map((skill) =>
              skill.skillId === skillId && skill.status === "running"
                ? { ...skill, status: "done" }
                : skill,
            ),
          }),
        ),
      };
    }

    case "ai_response": {
      const id = streamRowId(text(data.streamId));
      const usage = (data.usage ?? {}) as Record<string, unknown>;
      return {
        ...running,
        settledIds: [...running.settledIds, text(data.id)].filter(Boolean),
        rows: upsert(
          running.rows,
          id,
          () => thinkerSeed(id, null, text(data.author)),
          (row) => ({
            ...row,
            author: text(data.author) || row.author,
            text: text(data.content) || row.text,
            status: "done",
            settledId: maybe(data.id),
            usage: {
              totalTokens: digits(usage.totalTokens),
              totalCost: digits(usage.totalCost),
              model: maybe(usage.model) ?? row.model,
              durationMs: digits(usage.durationMs),
            },
          }),
        ),
      };
    }

    /** The REAL moderator event. `mergedFrom` is the attribution kms lost (W8-9). */
    case "merged_response": {
      const id = moderatorRowId(text(data.streamId));
      const mergedFrom = Array.isArray(data.mergedFrom) ? data.mergedFrom.length : 0;
      return {
        ...running,
        settledIds: [...running.settledIds, text(data.id)].filter(Boolean),
        rows: upsert(
          running.rows,
          id,
          () => moderatorSeed(id),
          (row) => ({
            ...row,
            author: text(data.author) || "Moderator",
            text: text(data.content) || row.text,
            status: "done",
            settledId: maybe(data.id),
            reason: mergedFrom > 0 ? `${mergedFrom}` : row.reason,
            usage: {
              totalTokens: null,
              totalCost: null,
              model: null,
              durationMs: digits(data.durationMs),
            },
          }),
        ),
      };
    }

    case "thinker_skipped":
      return {
        ...running,
        notices: [
          ...running.notices,
          { kind: "skipped", text: `${text(data.thinkerId)}: ${text(data.reason)}`.trim() },
        ],
      };

    case "thinker_empty": {
      const id = streamRowId(text(data.streamId));
      return {
        ...running,
        rows: upsert(
          running.rows,
          id,
          () => thinkerSeed(id, maybe(data.thinkerId), text(data.thinkerId)),
          (row) => ({ ...row, status: "empty", reason: maybe(data.reason) }),
        ),
      };
    }

    case "thinker_error": {
      const id = streamRowId(text(data.streamId));
      return {
        ...running,
        rows: upsert(
          running.rows,
          id,
          () => thinkerSeed(id, maybe(data.thinkerId), text(data.thinkerId)),
          (row) => ({ ...row, status: "error", reason: maybe(data.error) }),
        ),
      };
    }

    case "thinker_revision_start":
      return {
        ...running,
        notices: [...running.notices, { kind: "revision", text: text(data.thinkerId) }],
      };

    case "thinker_revision_complete":
      return running;

    case "moderator_start":
    case "moderator_synthesis_start":
      return {
        ...running,
        round: running.round ?? { current: 0, max: digits(data.maxRounds) ?? 3 },
      };

    case "moderator_evaluation_start":
      return {
        ...running,
        round: { current: digits(data.round) ?? 1, max: digits(data.maxRounds) ?? 3 },
      };

    case "moderator_evaluation_complete":
      return {
        ...running,
        notices: [...running.notices, { kind: "moderator", text: text(data.overallAssessment) }],
      };

    case "moderator_evaluation_failed":
    case "moderator_error":
      return {
        ...running,
        notices: [...running.notices, { kind: "error", text: text(data.error) }],
      };

    case "moderator_max_rounds":
      return {
        ...running,
        notices: [...running.notices, { kind: "moderator", text: "max-rounds" }],
      };

    case "moderator_reinstruction":
      return {
        ...running,
        notices: [
          ...running.notices,
          { kind: "moderator", text: `${text(data.thinkerId)}: ${text(data.reason)}`.trim() },
        ],
      };

    case "moderator_skipped":
      return {
        ...running,
        notices: [...running.notices, { kind: "moderator", text: text(data.reason) }],
      };

    /** Declared by kms and never emitted. Handled anyway, so a server change cannot regress. */
    case "moderator_synthesis":
      return running;

    case "early_stop":
      return {
        ...running,
        notices: [...running.notices, { kind: "early_stop", text: text(data.reason) }],
      };

    case "complete":
      return {
        ...running,
        running: false,
        complete: true,
        rows: running.rows.map((row) =>
          row.status === "streaming" ? { ...row, status: "done" } : row,
        ),
      };

    case "error":
      return {
        ...running,
        running: false,
        error: text(data.error) || "The assistant could not answer",
      };

    default:
      return {
        ...running,
        notices: [...running.notices, { kind: "unknown", text: event.name }],
      };
  }
}

/** What the status line says while a turn is in flight. Keys are translated by the caller. */
export function streamPhase(state: StreamState): "idle" | "thinking" | "moderating" | "done" {
  if (state.error) {
    return "done";
  }
  if (!state.running && state.complete) {
    return "done";
  }
  if (!state.running) {
    return "idle";
  }
  return state.rows.some((row) => row.kind === "moderator") ? "moderating" : "thinking";
}
